import { useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Navigate,
  Route,
  Routes as ReactRouterRoutes,
  useLocation,
} from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { useMatchBreakpoint } from '@hivebrite/design-system/Theme';

import {
  withTopicContext,
  TopicProvider,
} from 'hb-react/frontoffice/contexts/TopicContext';
import withDefaultAppContexts from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import * as FollowingsActions from 'hb-react/shared/apps/Topics/actions/followings';
import * as PostsActions from 'hb-react/shared/apps/Topics/actions/posts';
import * as TopicsActions from 'hb-react/shared/apps/Topics/actions/topics';
import TopicHeaderV2 from 'hb-react/shared/apps/Topics/components/TopicHeaderV2';
import { TopicHeaderDesktop } from 'hb-react/shared/apps/Topics/components/TopicHeaderV3/TopicHeaderDesktop';
import { TopicHeaderMobile } from 'hb-react/shared/apps/Topics/components/TopicHeaderV3/TopicHeaderMobile';
import { getGroupHeaderMembers } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/utils/getGroupHeaderMembers';
import { getGroupHeaderSocials } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/utils/getGroupHeaderSocials';
import {
  currentPageDisplayedPublicly,
  dynamicTopicResourcePath,
  redirectFromPublicRoot,
} from 'hb-react/shared/apps/Topics/utils';
import useGroupNavigationTheme from 'hb-react/shared/hooks/theme/useGroupNavigationTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { topicRoutes } from './utils/routesUtils';

const RedesignedHeader = (props) => {
  const isMobile = useMatchBreakpoint(['s']);
  const groupNavigationTheme = useGroupNavigationTheme();

  const {
    currentUser,
    followings: currentStateFollowings,
    socialLinks,
    topic,
  } = props;

  const members = useMemo(
    () =>
      getGroupHeaderMembers({
        currentUser,
        currentStateFollowings,
        topic,
      }),
    [currentUser, currentStateFollowings, topic],
  );

  const socials = useMemo(
    () =>
      getGroupHeaderSocials({
        socialLinks,
        topic,
      }),
    [socialLinks, topic],
  );

  const totalMembers =
    currentUser &&
    // Don't display the total members if there are no members
    // if no members, this can also mean that the topic is private
    members?.length > 0
      ? currentStateFollowings.get('total')
      : undefined;

  const Header = isMobile ? TopicHeaderMobile : TopicHeaderDesktop;

  return (
    <ReactRouterRoutes>
      {topicRoutes().map(({ path }) => (
        <Route
          path={path}
          key={path}
          element={
            <ThemeProvider
              theme={groupNavigationTheme.themeName}
              fontBrightness={groupNavigationTheme.fontBrightness}
            >
              {/* TODO: change the old logic to wrap the component within the ReactRouterRoutes once flagUjbGroupHeaderRedesign is removed */}
              <Header
                {...props}
                members={members}
                socials={socials}
                totalMembers={totalMembers}
              />
            </ThemeProvider>
          }
        />
      ))}
    </ReactRouterRoutes>
  );
};

const Header = ({
  currentUser,
  topic,
  flags,
  followings,
  following,
  currentNetwork,
  socialLinks,
  topicsActions,
  topicMenuRef,
  unfollowTopic,
  updateSettings,
}) => (
  <ReactRouterRoutes>
    {topicRoutes({ flags }).map(({ path }) => (
      <Route
        path={path}
        key={path}
        element={
          <TopicHeaderV2
            members={followings}
            {...{
              following,
              currentNetwork,
              currentUser,
              socialLinks,
              topic,
              topicsActions,
              topicMenuRef,
              unfollowTopic,
              updateSettings,
            }}
          />
        }
      />
    ))}
  </ReactRouterRoutes>
);

const TopicRoutes = ({ flags, subdomainNetworkUrl, topic }) => {
  const topicRoutesArray = useMemo(
    () =>
      topicRoutes({ flags }).map(
        ({
          path,
          component: Component,
          Wrapper = ({ children }) => children,
        }) => (
          <Route
            path={`${path}`}
            key={path}
            element={
              <Wrapper>
                <Component
                  flags={flags}
                  topic={topic}
                  subdomainNetworkUrl={subdomainNetworkUrl}
                />
              </Wrapper>
            }
          />
        ),
      ),
    [topic],
  );

  return <ReactRouterRoutes>{topicRoutesArray}</ReactRouterRoutes>;
};

const TopicShowContainer = ({
  currentNetwork,
  currentUser,
  following,
  followings,
  socialLinks,
  subdomainNetworkUrl,
  topic,
  topicId,
  topicsActions,
  whitelabelURL,
  topicMenuRef,
  posts_actions: postActions,
  followingsActions,
}) => {
  const { pathname } = useLocation();
  const flags = useFeatureFlag();

  const prevTopic = useRef({ topicId }).current;

  useEffect(() => {
    postActions.initPosts();
    topicsActions.fetchTopicIfNeeded(topicId, { whitelabelURL, flags });
  }, []);

  useEffect(() => {
    if (prevTopic.topicId !== topicId) {
      topicsActions.fetchTopicIfNeeded(topicId, { whitelabelURL, flags });
    }

    return () => {
      prevTopic.topicId = topicId;
    };
  }, [topicId]);

  const unfollowTopic = () =>
    followingsActions.unfollowTopic(topicId, following);

  const updateSettings = (following, params) =>
    followingsActions.updateSettings(topicId, following, params);

  if (!topic) {
    return <Loader size={50} />;
  }

  if (redirectFromPublicRoot(pathname, whitelabelURL, topic)) {
    return (
      <Navigate
        to={dynamicTopicResourcePath(topic.landing_page, {
          whitelabelURL,
          topicId,
        })}
      />
    );
  }

  if (
    !currentUser &&
    !currentPageDisplayedPublicly(pathname, whitelabelURL, topic)
  ) {
    return null;
  }

  const TopicHeader = flags.flagUjbGroupHeaderRedesign
    ? RedesignedHeader
    : Header;

  return (
    <TopicProvider topic={topic}>
      <TopicHeader
        currentUser={currentUser}
        topic={topic}
        flags={flags}
        followings={followings}
        following={following}
        currentNetwork={currentNetwork}
        socialLinks={socialLinks}
        topicsActions={topicsActions}
        topicMenuRef={topicMenuRef}
        unfollowTopic={unfollowTopic}
        updateSettings={updateSettings}
      />
      <Box ml={0} pb={40} display="block">
        <TopicRoutes
          flags={flags}
          subdomainNetworkUrl={subdomainNetworkUrl}
          topic={topic}
        />
      </Box>
    </TopicProvider>
  );
};

const mapStateToProps = (state, props) => {
  const { currentUser, topicId } = props;

  const topic = state.topics.getIn(['topics', topicId.toString()]);

  const following = state.followings.get('followings').find(
    (following) =>
      following.get('topic_id') == topicId && // eslint-disable-line eqeqeq
      following.get('user_id') == currentUser?.id, // eslint-disable-line eqeqeq
  );

  return {
    following: following ? following.toJS() : null,
    followings: state.followings,
    socialLinks: state.social_links,
    topic: topic ? topic.toJS() : null,
    topicId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  followingsActions: bindActionCreators(FollowingsActions, dispatch),
  topicsActions: bindActionCreators(TopicsActions, dispatch),
  posts_actions: bindActionCreators(PostsActions, dispatch),
});

export default compose(
  withDefaultAppContexts,
  withTopicContext,
  connect(mapStateToProps, mapDispatchToProps),
)(TopicShowContainer);
